import { Plus, Edit, Trash, Power, RefreshCw, Clock, AlertCircle, Check, Layers } from "lucide-react";
import { useEffect, useState } from "react";
import { AllAddonSession, DeleteAddonSession, ActivateAddonSession, DeactivateAddonSession } from "../components/Api.jsx";
import UpdateSessionSlot from "../components/dashboard/AddonSession/UpdateSessionSlot.jsx";
import CreateSessionSlot from "../components/dashboard/AddonSession/CreateSessionSlot.jsx";
import Pagination from "../components/Pagination.jsx";

// New component for session details popup
function SessionDetailsPopup({ session, onClose }) {
  if (!session) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 overflow-hidden rounded-lg bg-white shadow-xl border border-gray-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">
              Session Details
            </h2>
            <button
              onClick={onClose}
              className="rounded-full p-1 hover:bg-gray-100"
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{session.sessionName}</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500">Price</p>
                <p className="font-medium">₹ {session.price}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500">Duration</p>
                <p className="font-medium">{session.durationMinutes} minutes</p>
              </div>

              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500">Session Count</p>
                <p className="font-medium">{session.sessionCount}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500">Status</p>
                <p className={`font-medium ${session.isActive ? 'text-green-600' : 'text-red-600'}`}>
                  {session.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <p className="text-gray-800">{session.description || '-'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AddonSessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenForCreate, setIsModalOpenCreate] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  useEffect(() => {
    setIsMounted(true);
    fetchData();
  }, []);

  const fetchData = (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }

    AllAddonSession().then((result) => {
      // setSessions(result.data);
      // console.log(result.data);
      const sortedSessions = result.data.sort((a, b) => a.id - b.id);
      setSessions(sortedSessions);
      console.log(sortedSessions);
    }).catch((error) => {
      console.error('Error fetching sessions:', error);
    }).finally(() => {
      if (isRefresh) {
        setRefreshing(false);
      }
    });
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setIsModalOpen(true);
    console.log("open");
  };

  const handleCreateNewSessionClick = () => {
    setIsModalOpenCreate(true);
  };

  const handleViewDetailsClick = (session) => {
    setSelectedSession(session);
    setIsDetailsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSession(null);
    setIsModalOpenCreate(false);
    setIsDetailsModalOpen(false);
    fetchData();
  };

  const handleDelete = (id) => {
    DeleteAddonSession(id).then(result => {
      console.log(result.data);
      fetchData();
    });
    console.log(id);
  };

  const handleToggleStatus = async (id, isActive) => {
    try {
      if (isActive) {
        await DeactivateAddonSession(id);
      } else {
        await ActivateAddonSession(id);
      }
      fetchData(); // Refresh the data after status change
    } catch (error) {
      console.error('Error toggling session status:', error);
    }
  };

  // Function to truncate description to 1 line
  const truncateDescription = (description) => {
    if (!description) return '';
    return description.length > 60 ? `${description.substring(0, 60)}...` : description;
  };

  if (!isMounted) {
    return <div className="text-center p-8">Loading addon sessions...</div>;
  }

  return (
    <div className="min-h-screen bg-white text-black p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-teal-100 p-3 rounded-lg">
                  <Clock className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Create additional sessions to be added with the ongoing plan, allowing users to purchase extra interaction time with trainers. The time duration of these sessions are in minutes.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh sessions"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={() => handleCreateNewSessionClick()}
                aria-label="Create new addon session"
              >
                <Plus className="w-5 h-5" />
                <span>New Session</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {sessions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((session, index) => (
            <div
              key={session.id}
              className="bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-blue-300"
              title={session.description}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-semibold">{session.sessionName}</h3>
                  <p className="text-xl font-bold text-indigo-600 mt-1">
                    ₹ {session.price}
                  </p>
                  <div
                    className="text-gray-800 text-sm mt-2"
                  >
                    {truncateDescription(session.description)}
                  </div>
                  <p className="text-gray-700 text-sm mt-2">
                    Duration: {session.durationMinutes} mins
                  </p>
                  <p className="text-gray-700 text-sm">
                    Sessions: {session.sessionCount}
                  </p>
                  <p className="text-gray-700 text-sm">
                    Status: {session.isActive ? "Active" : "Inactive"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    onClick={() => handleSessionClick(session)}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    className={`p-2 rounded-lg ${session.isActive
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-600'
                      }`}
                    onClick={() => handleToggleStatus(session.id, session.isActive)}
                    title={session.isActive ? 'Deactivate Session' : 'Activate Session'}
                  >
                    <Power className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <button
                className="bg-blue-600 text-white p-2 rounded-lg mt-3 text-sm w-full hover:bg-blue-700 transition-all duration-200"
                onClick={() => handleViewDetailsClick(session)}
              >
                View Details
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10">
          <p className="text-gray-500 text-lg">No sessions found.</p>
        </div>
      )}

      {sessions.length > 0 && (
        <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray(sessions) ? sessions.length : (sessions?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray(sessions) ? sessions.length : (sessions?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
      )}

      {isMounted && isModalOpen && selectedSession && (
        <UpdateSessionSlot session={selectedSession} onClose={closeModal} onSuccess={() => showToast('Session slot updated successfully')} />
      )}

      {isMounted && isModalOpenForCreate && (
        <CreateSessionSlot onClose={closeModal} onSuccess={() => showToast('Session slot created successfully')} />
      )}

      {isMounted && isDetailsModalOpen && selectedSession && (
        <SessionDetailsPopup session={selectedSession} onClose={closeModal} />
      )}
    </div>
  );
}